'use client';

import React, { useState, useEffect } from 'react';
import { ChevronRightIcon } from 'lucide-react';
import { useFiltersStore, useBookingStore, useAuthStore } from '../../../BookingJourney/context';
import warningIcon from '../../assets/icons/warning_full.svg';
import { validateLandingFilters } from '../../../BookingJourney/components/landing/hero/validation';
import { useResetJourney } from '../../../BookingJourney/hooks/useResetJourney';
import { checkExpiration } from '../../../BookingJourney/helpers/checkExpiration';
import { fillStrategy } from '../../../BookingJourney/types/estimatedPrice';
import { useSegmentEvent } from '../../../../hooks/useSegmentEvent';
import { SegmentEvents } from '../../../../constants/segment-events';
import { trackStandardizedEvent, getServiceCategory, getBookingFlowType, ServiceType } from '../../../../utils/segment/trackStandardizedEvent';
import DateSearchField from './DateSearchField';
import { PetsSearchField } from './PetsSearchField';

// Error message component for reusability
const ErrorMessage = ({
  message = "Required: Service type, dates, and pets."
}) => <div className="flex p-[8px] px-[16px] justify-center items-center gap-[8px] self-stretch rounded-[8px] border-solid border-2 border-[#FF5867] bg-white" data-sentry-component="ErrorMessage" data-sentry-source-file="index.tsx">
    <img src={warningIcon} alt="Warning" className="w-4 h-4" />
    <p className="text-[#FF5867] font-['Hero New'] text-[12px] font-normal leading-[16px] tracking-[0px] m-0">
      {message}
    </p>
  </div>;
type Props = {
  selectedService?: string;
};
export const HomeBookingFilters = ({
  selectedService
}: Props) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Get state from Zustand stores
  const {
    filters
  } = useFiltersStore();
  const setFilters = useFiltersStore(state => state.setFilters);

  // Get booking state
  const {
    schedule,
    isOnDemand,
    isMarketplaceEnabled,
    createdAt,
    setJourney,
    setSchedule
  } = useBookingStore();
  const {
    selectedDays
  } = schedule;

  // Get auth state
  const sessionToken = useAuthStore(state => state.session.sessionToken);

  // Get reset journey hooks
  const {
    resetBookingJourneyAndClearStorage,
    resetAuthState
  } = useResetJourney();

  // Get segment tracking
  const {
    trackEvent
  } = useSegmentEvent();

  // Update service when selectedService changes - only run once when selectedService changes
  useEffect(() => {
    if (selectedService) {
      setFilters({
        service: selectedService as ServiceType
      });
    }
  }, [selectedService, setFilters]);

  // Validation helper
  const validateFields = () => {
    if (!selectedService) {
      setErrorMessage('Please select a service type.');
      return false;
    }
    const hasValidDates = selectedService === 'walk' || selectedService === 'drop-in' ? selectedDays?.length > 0 : filters.startDate && filters.endDate;
    if (!hasValidDates) {
      setErrorMessage('Please select your dates.');
      return false;
    }
    if (!(filters.dogsNumber > 0 || filters.catsNumber > 0)) {
      setErrorMessage('Please add at least one pet.');
      return false;
    }
    return true;
  };
  const getNavigationUrl = () => {
    if (isMarketplaceEnabled && !isOnDemand) {
      return filters.service === 'boarding' ? '/marketplace-boarding' : '/marketplace-sitting';
    }
    if (isOnDemand) {
      return '/on-demand';
    }
    return '/search';
  };
  const handleContinue = () => {
    if (!validateFields()) return;
    const parsedFilters = validateLandingFilters({
      ...filters,
      selectedDays
    }, true, false);
    if (!parsedFilters.success) return;
    setErrorMessage(null);
    try {
      const {
        isExpired
      } = checkExpiration(createdAt);
      if (isExpired) {
        resetAuthState();
        resetBookingJourneyAndClearStorage();
      }

      // Track standardized landing page click event
      trackStandardizedEvent(trackEvent, SegmentEvents.LandingPageClick, {
        service_type: filters.service as ServiceType,
        service_category: getServiceCategory(filters.service as ServiceType),
        booking_flow_type: getBookingFlowType(isMarketplaceEnabled)
      });
      if (isOnDemand || isMarketplaceEnabled) {
        setSchedule({
          fillStrategy: fillStrategy.pet_parent_choice,
          repeat: false,
          days: []
        });
        setJourney({
          step: sessionToken && !isExpired ? 'RequestService' : 'CreateAccount'
        });
      }
      window.location.href = getNavigationUrl();
    } catch (error) {
      if (error && typeof error === 'object' && 'name' in error && error.name !== 'CanceledError') {
        setErrorMessage('An error occurred. Please try again.');
      }
    }
  };
  return <div className='w-full py-0' data-sentry-component="HomeBookingFilters" data-sentry-source-file="index.tsx">
      <div className="relative md:grid md:grid-cols-12 flex flex-col gap-[12px] md:gap-0 px-[12px] md:pr-4 md:pl-8 pt-[10px] pb-[10px] md:py-3 items-start md:items-center self-stretch rounded-[12px] md:rounded-[48px] bg-white shadow-[0px_2px_8px_0px_rgba(0,0,0,0.15)]">
        {/* Date field */}
        <div className="w-full md:w-auto md:col-span-5 flex items-center relative md:pl-0 overflow-visible">
          <DateSearchField showArrow={true} data-sentry-element="DateSearchField" data-sentry-source-file="index.tsx" />
        </div>
        
        {/* Horizontal divider for mobile - positioned in the middle of the gap */}
        <div className="w-full h-[1px] my-[-6px] opacity-10 bg-[#222] md:hidden"></div>
        
        {/* Pet field */}
        <div className="w-full md:w-auto md:col-span-5 flex items-center md:pl-4 relative">
          {/* Vertical separator (only visible on desktop) */}
          <div className="hidden md:block absolute left-0 top-1/2 -translate-y-1/2 w-[1px] h-[32px] opacity-10 bg-[#222]"></div>
          <PetsSearchField data-sentry-element="PetsSearchField" data-sentry-source-file="index.tsx" />
        </div>

        {/* Horizontal divider for mobile - positioned in the middle of the gap */}
        <div className="w-full h-[1px] my-[-6px] opacity-10 bg-[#222] md:hidden"></div>

        {/* Error message for mobile */}
        <div className="w-full md:hidden">
          {errorMessage && <ErrorMessage message={errorMessage} />}
        </div>
        
        
        {/* Continue button */}
        <div className="w-full md:w-auto md:col-span-2 flex justify-center md:justify-end md:pl-4"> 
          <button className="w-full md:w-auto flex h-[48px] px-[30px] md:px-[16px] lg:px-[30px] py-[12px] justify-center items-center gap-[4px] rounded-[24px] bg-[#2866D3] text-white text-center whitespace-nowrap border-none outline-none font-['Hero New'] text-[14px] font-bold leading-[20px] tracking-[0px] cursor-pointer" onClick={handleContinue}>
            Continue
            <ChevronRightIcon className="w-4 h-4" strokeWidth={3} data-sentry-element="ChevronRightIcon" data-sentry-source-file="index.tsx" />
          </button>
        </div>
      </div>
      
      {/* Error Box for desktop - only shown when there's an error */}
      {errorMessage && <div className="hidden md:block mt-3">
          <ErrorMessage message={errorMessage} />
        </div>}
      
    </div>;
};
export default HomeBookingFilters;